/**
 * Joining finished executions back into the drums they came off.
 *
 * Execution History lists one row per ExecutionLog — one per TASK. The queue
 * editor does not: it shows one row per physical pass of the machine, because
 * two products extruded onto one drum are one thing that happened, listed twice
 * only because the data is per product.
 *
 * History was missing both joins, and on 2026-09-15 that cost a night's work.
 * An N2XH 5x6 and an N2XH 3x6 were launched as a merge execution: one drum,
 * 10 300 m of the first product then 2 060 m of the second. The history table
 * grouped its rows by job, so the two halves of that one drum appeared in
 * different places on the screen with nothing relating them. The operator
 * stopped at 10 300 — the only number anything showed them — and the 3x6 was
 * recorded with 4.3 m and 7.7 m on two of its three conductors.
 *
 * Same two joins as helpers/machineQueueBlocks.js, on the other side of time:
 *
 *   MERGE   logs that wrote to the SAME output token are one drum. Kept as
 *           separate rows (each has its own operators, its own minutes and its
 *           own actions) but pulled together under one header that states the
 *           drum's real total.
 *
 *   RUN     consecutive drums sharing a `GB::<phase>::<kind>` prefix are one
 *           group-by launch — same colour or same wire Ø, deliberately queued
 *           together. Not a merge: a bobbin each.
 *
 * History runs newest-first, so a run's members arrive in reverse. That changes
 * nothing: they are still consecutive, which is all the grouping asks.
 *
 * Pure — no React, no fetching — so it is tested directly.
 */

import { getJobColor, parseGroupByKey } from './machineQueueBlocks.js';

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = v => String(v ?? '');

/**
 * Two logs that are one drum.
 *
 * Same output token, DIFFERENT task. The second half matters: re-queuing an
 * execution and finishing it again leaves two logs on the same token and the
 * same task, and that is a correction, not a merge. A cancelled log is never
 * part of a drum for the same reason.
 */
export function sameDrum(a, b) {
  if (!a || !b) return false;
  if (a.cancelled || b.cancelled) return false;
  const ta = str(a.tokenId);
  const tb = str(b.tokenId);
  if (!ta || !tb || ta !== tb) return false;
  return str(a.taskId) !== str(b.taskId);
}

/** The products on a drum, first-seen order, one entry per job. */
function productsOf(logs) {
  const seen = new Set();
  const out = [];
  for (const log of logs) {
    const jobId = str(log.jobId);
    if (seen.has(jobId)) continue;
    seen.add(jobId);
    out.push({
      key: `${jobId}-${str(log.taskId)}`,
      jobId,
      jobCode: log.jobCode || null,
      productLabel: log.productLabel || null,
      plannedLength: num(log.plannedLength),
      producedLength: num(log.producedLength)
    });
  }
  return out;
}

/**
 * Group the logs into drums, keeping the order they were given in.
 *
 * A block takes the position of its FIRST member, and the rest are lifted out
 * of wherever they were. The halves of a merge are normally adjacent anyway —
 * one pass of the machine finishes them within minutes — but nothing guarantees
 * it, and a drum split across the page is exactly the failure this exists to
 * prevent.
 */
export function buildExecutionBlocks(logs) {
  const list = (Array.isArray(logs) ? logs : []).filter(Boolean);
  const used = new Set();
  const blocks = [];

  list.forEach((log, i) => {
    if (used.has(i)) return;
    used.add(i);

    const members = [log];
    for (let k = i + 1; k < list.length; k += 1) {
      if (used.has(k)) continue;
      if (sameDrum(log, list[k])) {
        used.add(k);
        members.push(list[k]);
      }
    }

    const plannedTotal = members.reduce((s, m) => s + num(m.plannedLength), 0);
    // One drum has ONE length on it. Every log of a merge carries the same
    // physical token, so its produced metres are that token's share — they add
    // up to what came off the machine.
    const producedTotal = members.reduce((s, m) => s + num(m.producedLength), 0);

    blocks.push({
      id: `drum-${members.map(m => m._id || m.taskId).join('|')}`,
      logs: members,
      tokenId: str(log.tokenId) || null,
      isMerge: members.length > 1,
      products: productsOf(members),
      plannedTotal,
      producedTotal,
      groupByKey: log.groupByKey || null,
      borderColor: getJobColor(str(log.jobId))
    });
  });

  return blocks;
}

/** `GB::phase::kind` — the prefix shared by every drum of one launch. */
export function blockGroupByPrefix(block) {
  const key = block?.groupByKey;
  if (!key) return null;
  const parts = str(key).split('::');
  if (parts.length < 4) return null;
  return parts.slice(0, 3).join('::');
}

/**
 * The rows to render: drums in order, each told whether it opens a group-by run
 * and whether it is the first drum of its product on screen.
 *
 * `showJob` repeats the product name whenever the drum above it belongs to a
 * different product. The queue editor writes it once per job because a job's
 * blocks sit together there; here the order is time, so the same product comes
 * back after other work and a name written only the first time would caption
 * the wrong rows.
 */
export function buildHistoryRows(logs) {
  const blocks = buildExecutionBlocks(logs);

  // Runs: consecutive blocks with the same GB prefix.
  const runOf = [];
  const runIsGB = [];
  let rid = -1;
  let prev = null;
  blocks.forEach((block, i) => {
    const pref = blockGroupByPrefix(block);
    if (!(pref && pref === prev)) {
      rid += 1;
      runIsGB[rid] = !!pref;
    }
    runOf[i] = rid;
    prev = pref;
  });

  const runSize = {};
  runOf.forEach(r => {
    runSize[r] = (runSize[r] || 0) + 1;
  });

  const seenRuns = new Set();

  return blocks.map((block, index) => {
    const run = runOf[index];
    const isGroupByRun = !!runIsGB[run];

    const primaryJobId = block.products[0]?.jobId || null;
    const prevJobId = blocks[index - 1]?.products?.[0]?.jobId || null;
    const showJob =
      block.isMerge || !primaryJobId || primaryJobId !== prevJobId;

    const opensRun = isGroupByRun && !seenRuns.has(run);
    if (isGroupByRun) seenRuns.add(run);

    return {
      ...block,
      index,
      showJob,
      runId: run,
      isGroupByRun,
      // only the first drum of a run carries the heading
      opensRun,
      runSize: runSize[run] || 1,
      groupBy: isGroupByRun ? parseGroupByKey(block.groupByKey) : null
    };
  });
}
